
import { useState, useRef, ChangeEvent } from 'react';
import { Vendor, UploadProgress, UploadStatus } from '@/types';
import { Button } from '@/components/ui/button';
import { Upload, FileText, History } from 'lucide-react';
import { getPresignedUrl, uploadFileWithPresignedUrl } from '@/services/api';
import UploadStatusComponent from './UploadStatus';
import { useToast } from '@/hooks/use-toast';

interface FileUploadProps {
  selectedVendor: Vendor | null;
  onSuccessAction?: () => void;
}

const FileUpload = ({ selectedVendor, onSuccessAction }: FileUploadProps) => {
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    status: UploadStatus.IDLE,
    progress: 0,
    fileName: ''
  });
  
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const resetUpload = () => {
    console.log('Resetting upload state');
    setUploadProgress({
      status: UploadStatus.IDLE,
      progress: 0,
      fileName: ''
    });
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    
    if (!files || files.length === 0) {
      console.log('No file selected or file selection canceled');
      resetUpload();
      return;
    }
    
    const file = files[0];
    console.log(`File selected: ${file.name}, type: ${file.type}, size: ${file.size} bytes`);
    
    // Check if it's a CSV file
    if (!file.name.toLowerCase().endsWith('.csv')) {
      console.error('Invalid file type selected:', file.type);
      setUploadProgress({
        status: UploadStatus.ERROR,
        progress: 0,
        fileName: file.name,
        error: 'Please select a CSV file'
      });
      return;
    }
    
    setSelectedFile(file);
    setUploadProgress({
      status: UploadStatus.SELECTED,
      progress: 0,
      fileName: file.name
    });
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      console.error('No file selected for upload');
      setUploadProgress({
        status: UploadStatus.ERROR,
        progress: 0,
        fileName: '',
        error: 'Please select a file to upload'
      });
      return;
    }
    
    if (!selectedVendor) {
      console.error('No vendor selected for upload');
      setUploadProgress({
        status: UploadStatus.ERROR,
        progress: 0,
        fileName: selectedFile.name,
        error: 'Please select a vendor first'
      });
      return;
    }
    
    console.log(`Starting upload process for ${selectedFile.name} (vendor: ${selectedVendor.vendorName})`);
    
    try {
      // Update status to uploading
      setUploadProgress({
        status: UploadStatus.UPLOADING,
        progress: 0,
        fileName: selectedFile.name
      });
      
      console.log('Requesting presigned URL...');
      // Get the presigned URL
      const { presignedUrl } = await getPresignedUrl(
        selectedFile.name,
        selectedVendor.vendorName
      );
      
      console.log('Presigned URL received, beginning file upload...');
      // Upload the file with progress tracking
      await uploadFileWithPresignedUrl(
        presignedUrl, 
        selectedFile,
        selectedFile.name,
        selectedVendor.vendorName,
        (progressPercentage) => {
          console.log(`Upload progress update: ${progressPercentage}%`);
          setUploadProgress(prev => ({
            ...prev,
            progress: progressPercentage
          }));
        }
      );
      
      console.log('Upload completed successfully');
      // Update status to success
      setUploadProgress({
        status: UploadStatus.SUCCESS,
        progress: 100,
        fileName: selectedFile.name,
        message: `File "${selectedFile.name}" was successfully uploaded for vendor "${selectedVendor.vendorName}"`
      });
      
      // Reset file input after success
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSelectedFile(null);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Upload failed:', errorMessage);
      console.error('Error details:', error);
      
      setUploadProgress({
        status: UploadStatus.ERROR,
        progress: 0,
        fileName: selectedFile.name,
        error: errorMessage
      });
    }
  };

  const handleGoToHistory = () => {
    console.log('Navigating to history tab');
    if (onSuccessAction) {
      onSuccessAction();
    }
  };

  const triggerFileSelect = () => {
    console.log('Triggering file select dialog');
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const isUploading = uploadProgress.status === UploadStatus.UPLOADING;
  const canUpload = selectedFile && selectedVendor && !isUploading;
  const showHistoryButton = uploadProgress.status === UploadStatus.SUCCESS;

  return (
    <div className="space-y-4">
      <div className="csv-drop-zone" onClick={triggerFileSelect}>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          ref={fileInputRef}
          className="hidden"
          disabled={isUploading}
        />
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="rounded-full bg-blue-100 p-3">
            <FileText size={24} className="text-primary" />
          </div>
          <div className="space-y-1 text-center">
            <p className="text-sm font-medium text-gray-900">
              {selectedFile 
                ? `Selected: ${selectedFile.name}` 
                : "Click to select a CSV file"}
            </p>
            <p className="text-xs text-gray-500">
              Only CSV files are accepted
            </p>
          </div>
        </div>
      </div>

      <UploadStatusComponent uploadProgress={uploadProgress} />
      
      <div className="flex gap-2 justify-end">
        {uploadProgress.status !== UploadStatus.IDLE && (
          <Button
            variant="outline"
            onClick={resetUpload}
            disabled={isUploading}
          >
            Reset
          </Button>
        )}
        
        {showHistoryButton && (
          <Button
            onClick={handleGoToHistory}
            className="flex items-center gap-2"
            variant="secondary"
          >
            <History size={16} />
            Go to History
          </Button>
        )}
        
        <Button
          onClick={handleUpload}
          disabled={!canUpload}
          className="flex items-center gap-2"
        >
          <Upload size={16} />
          Upload File
        </Button>
      </div>
    </div>
  );
};

export default FileUpload;
